import { NextPage } from "next";
import React, {
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import styles from "../../styles/GalleryAll.module.scss";
import { useTranslation } from "react-i18next";
import Spacer from "../../components/Spacer";
import Image from "next/image";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  QueryDocumentSnapshot,
  startAfter,
} from "@firebase/firestore";
import { db } from "../../utils/firebase";
import { NFTCollection, RCSANFTWithSnapshot } from "../../utils/types";
import { useIsMounted, usePrevious, useWindowSize } from "../../utils/hooks";
import { FilterSection } from "../../components/FilterSection";
import clsx from "clsx";
import { a, useSpring } from "react-spring";
import { nullAddress } from "../../utils/constants";
import { Loading } from "../../components/Loading";
import { GalleryCardItem } from "../../components/GalleryCardItem";
import { Waypoint } from "react-waypoint";
import { formatItems } from "../../utils/gallery";
import { useRouter } from "next/router";
import { Header, HeaderVariant } from "../../components/Header";
import { ParsedUrlQuery } from "querystring";
import { Meta } from "../../components/Meta";
import { MediaQueryContext } from "../../context/mediaQuery/context";

export enum StatusFilter {
  all,
  available,
}

export enum MediaTypeFilter {
  none,
  image,
  gif,
  video,
}

export interface FiltersState {
  statusFilter: StatusFilter;
  mediaTypeFilters: MediaTypeFilter[];
  selectedCollections: string[];
}

export type FiltersAction =
  | {
      type: "status";
      payload: StatusFilter;
    }
  | {
      type: "media-type";
      payload: MediaTypeFilter;
    }
  | {
      type: "collection";
      payload: string;
    }
  | {
      type: "update";
      payload: {
        status: StatusFilter;
        mediaTypeFilters: MediaTypeFilter[];
        selectedCollections: string[];
      };
    }
  | { type: "reset" };

export const filtersReducer = (
  state: FiltersState,
  action: FiltersAction
): FiltersState => {
  switch (action.type) {
    case "status": {
      return {
        ...state,
        statusFilter: action.payload,
      };
    }

    case "media-type": {
      return {
        ...state,
        mediaTypeFilters: state.mediaTypeFilters.includes(action.payload)
          ? state.mediaTypeFilters.filter((item) => item !== action.payload)
          : [...state.mediaTypeFilters, action.payload],
      };
    }

    case "collection": {
      return {
        ...state,
        selectedCollections: state.selectedCollections.includes(action.payload)
          ? state.selectedCollections.filter((item) => item !== action.payload)
          : [...state.selectedCollections, action.payload],
      };
    }

    case "update": {
      return {
        ...state,
        statusFilter: action.payload.status,
        mediaTypeFilters: action.payload.mediaTypeFilters,
        selectedCollections: action.payload.selectedCollections,
      };
    }

    case "reset": {
      return {
        ...state,
        statusFilter: StatusFilter.all,
        mediaTypeFilters: [],
        selectedCollections: [],
      };
    }

    default:
      return state;
  }
};

export const isInStatusFilter = (
  item: RCSANFTWithSnapshot,
  statusFilter: StatusFilter
) => {
  if (statusFilter === StatusFilter.available) {
    return item.creatorAddress === nullAddress && item.isLocked !== true;
  }

  return true;
};

export const isInMediaTypeFilter = (
  item: RCSANFTWithSnapshot,
  mediaTypeFilters: MediaTypeFilter[]
) => {
  if (mediaTypeFilters.length === 0) return true;

  const mimeType = (item.underlyingNftMimeType ?? "").toLowerCase();

  return mediaTypeFilters.some((mediaType) => {
    switch (mediaType) {
      case MediaTypeFilter.image:
        return mimeType !== "image/gif" && mimeType.startsWith("image/");
      case MediaTypeFilter.gif:
        return mimeType === "image/gif";
      case MediaTypeFilter.video:
        return mimeType.startsWith("video/");
      default:
        return true;
    }
  });
};

const isInCollection = (
  item: RCSANFTWithSnapshot,
  selectedCollections: string[]
) => {
  if (selectedCollections.length === 0) return true;

  return selectedCollections.includes(item.underlyingNftSymbol || "OTHERS");
};

export const parseUrlQuery = (
  query: ParsedUrlQuery
): { status: StatusFilter; mediaTypeFilters: MediaTypeFilter[] } => {
  const { status, mediaType = [] } = query;

  let nextStatus = StatusFilter.all;
  if (status && typeof status === "string") {
    nextStatus =
      parseInt(status as string) === 1
        ? StatusFilter.available
        : StatusFilter.all;
  }

  let nextMediaTypeFilters: MediaTypeFilter[] = [];
  if (mediaType) {
    if (typeof mediaType === "object") {
      nextMediaTypeFilters = mediaType.map<MediaTypeFilter>(
        // @ts-ignore
        (type) => MediaTypeFilter[MediaTypeFilter[type]]
      );
    } else if (typeof mediaType === "string") {
      // @ts-ignore
      nextMediaTypeFilters = [MediaTypeFilter[MediaTypeFilter[mediaType]]];
    }
  }

  return {
    status: nextStatus,
    mediaTypeFilters: nextMediaTypeFilters,
  };
};

const NUMBER_DOCS_PER_PAGE = 24;

const GalleryAll: NextPage = () => {
  const { t } = useTranslation();

  const [nfts, setNFTs] = useState<RCSANFTWithSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const {
    state: { tablet },
  } = useContext(MediaQueryContext);

  const [filters, filtersDispatch] = useReducer(filtersReducer, {
    statusFilter: StatusFilter.all,
    mediaTypeFilters: [],
    selectedCollections: [],
  });

  const router = useRouter();
  const [usedUrl, setUsedUrl] = useState(false);
  useEffect(() => {
    if (!router.isReady || usedUrl) return;

    const { collections = [] } = router.query;

    const { status, mediaTypeFilters } = parseUrlQuery(router.query);

    filtersDispatch({
      type: "update",
      payload: {
        status,
        mediaTypeFilters,
        selectedCollections:
          typeof collections === "object" ? collections : [collections],
      },
    });

    setUsedUrl(true);
  }, [router.query, router.isReady, usedUrl]);

  useEffect(() => {
    if (!router.isReady || !usedUrl) return;

    router
      .replace("/gallery/all", {
        query: {
          status: filters.statusFilter,
          mediaType: filters.mediaTypeFilters.map((item) => item),
          collections: filters.selectedCollections,
        },
      })
      .then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, router.isReady, usedUrl]);

  const [showFilters, setShowFilters] = useState(false);

  const loadData = useCallback(
    async (lastSnapshot?: QueryDocumentSnapshot) => {
      if (loadingMore) return;

      const queryConstraints = [limit(NUMBER_DOCS_PER_PAGE)];

      if (lastSnapshot) {
        setLoadingMore(true);
        queryConstraints.push(startAfter(lastSnapshot));
      }

      return getDocs(query(collection(db, "rcsa"), ...queryConstraints))
        .then((snapshot) => {
          const data = snapshot.docs.map(
            (doc) =>
              ({
                snapshot: doc,
                ...doc.data(),
              } as RCSANFTWithSnapshot)
          );

          setNFTs(lastSnapshot ? [...nfts, ...data] : data);

          setShouldLoadMore(data.length === NUMBER_DOCS_PER_PAGE);
        })
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [nfts, loadingMore]
  );

  useEffect(() => {
    loadData().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredNFTs = useMemo(
    () =>
      nfts.filter((item) => {
        return (
          !!item.tokenId &&
          isInStatusFilter(item, filters.statusFilter) &&
          isInMediaTypeFilter(item, filters.mediaTypeFilters) &&
          isInCollection(item, filters.selectedCollections)
        );
      }),
    [
      nfts,
      filters.selectedCollections,
      filters.statusFilter,
      filters.mediaTypeFilters,
    ]
  );
  const windowSize = useWindowSize();

  return (
    <div className={styles.container}>
      <Meta title={"Gallery - View all"} />

      <Header variant={HeaderVariant.light} />
      <Spacer height={40} className={"hideOnTablet"} />
      <Spacer height={34} className={"hideOnDesktop"} />
      <main className={"column"}>
        <div className={"row ai-center"}>
          <div
            className={clsx("outlineButton row ai-center", styles.filterButton)}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Image
              priority
              layout={"fixed"}
              src={showFilters ? "/arrow-2-green.svg" : "/filter.svg"}
              width={20}
              height={20}
              alt={"filter arrow icon"}
              style={{
                transform:
                  tablet && showFilters ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
            <Spacer width={8} />
            <div>{t("galleryAll.filter")}</div>
          </div>
          <Spacer width={24} />
          <NFTCounter />
        </div>

        <Spacer height={66} className={"hideOnTablet"} />
        <Spacer height={40} className={"hideOnDesktop"} />

        <div className={"rowDesktopColumnTablet"}>
          <FilterPanel
            showFilters={showFilters}
            filtersDispatch={filtersDispatch}
            filters={filters}
            tablet={tablet}
          />

          <Spacer width={showFilters ? 64 : 26} className={"hideOnTablet"} />
          <Spacer height={showFilters ? 40 : 0} className={"hideOnDesktop"} />

          <div
            className={clsx(styles.itemsContainer, {
              [styles.itemsContainerWithFilter]: showFilters,
            })}
          >
            {loading ? (
              <div
                className={"column ai-center jc-center"}
                style={{ height: 400 }}
              >
                <Loading />
              </div>
            ) : (
              <>
                <div className={styles.items}>
                  {filteredNFTs.length === 0 && !loading && !shouldLoadMore && (
                    <div
                      className={"column ai-center jc-center w-full"}
                      style={{ height: 400 }}
                    >
                      <div style={{ color: "#073937" }}>
                        {t("galleryAll.noData")}
                      </div>
                    </div>
                  )}
                  {filteredNFTs.map((item) => {
                    return (
                      <GalleryCardItem
                        item={item}
                        key={item.tokenId}
                        width={
                          tablet
                            ? Math.min((windowSize.width ?? 0) * 0.5, 230.56)
                            : 224.49
                        }
                        containerClassName={styles.galleryCardItem}
                      />
                    );
                  })}
                </div>

                {shouldLoadMore && !loadingMore && (
                  <Waypoint
                    onEnter={() => loadData(nfts[nfts.length - 1]?.snapshot)}
                  />
                )}

                {shouldLoadMore && (
                  <div
                    className={"column ai-center jc-center"}
                    style={{
                      height: 200,
                      opacity: loadingMore ? 1 : 0,
                      transition: "opacity 0.4s ease-out",
                    }}
                  >
                    <Loading />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <Spacer height={200} />
      </main>
    </div>
  );
};

export default GalleryAll;

interface FilterPanelProps {
  showFilters: boolean;
  filters: FiltersState;
  filtersDispatch: Dispatch<FiltersAction>;
  tablet: boolean;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  showFilters,
  filters,
  filtersDispatch,
  tablet,
}) => {
  const [toggle, setToggle] = useState(false);
  const { t } = useTranslation();
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const mounted = useIsMounted();
  const previous = usePrevious(showFilters);
  const { width, opacity, height } = useSpring({
    from: { width: 0, opacity: 0, height: 0 },
    to: {
      width: showFilters ? filterPanelRef.current?.clientWidth ?? 0 : 0,
      height: showFilters ? filterPanelRef.current?.clientHeight ?? 0 : 0,
      opacity: showFilters ? 1 : 0,
    },
    immediate: !mounted,
    onRest: () => setToggle(!toggle),
  });

  const statusFilters = useMemo(
    () => [
      { text: t("galleryAll.all"), type: StatusFilter.all },
      { text: t("galleryAll.available"), type: StatusFilter.available },
    ],
    [t]
  );
  const mediaTypeFilters = useMemo(
    () => [
      { text: t("galleryAll.image"), type: MediaTypeFilter.image },
      { text: t("galleryAll.gif"), type: MediaTypeFilter.gif },
      { text: t("galleryAll.video"), type: MediaTypeFilter.video },
    ],
    [t]
  );

  const nextWidth = useMemo(
    () =>
      tablet
        ? "100%"
        : showFilters && previous === showFilters
        ? "auto"
        : width,
    [tablet, width, showFilters, previous]
  );

  const nextHeight = useMemo(
    () =>
      tablet
        ? showFilters && previous === showFilters
          ? "auto"
          : height
        : "auto",
    [tablet, showFilters, previous, height]
  );

  return (
    <a.div
      style={{
        width: nextWidth,
        height: nextHeight,
        opacity,
        overflow: "hidden",
      }}
    >
      <div className={styles.filterPanel} ref={filterPanelRef}>
        <FilterSection name={t("galleryAll.status")}>
          <div className={"row ai-center"}>
            {statusFilters.map((item, index) => {
              const selected = item.type === filters.statusFilter;

              return (
                <React.Fragment key={`status_filter_${item.type}`}>
                  <div
                    className={clsx(
                      "outlineButton row ai-center",
                      styles.filterItemButton
                    )}
                    onClick={() =>
                      filtersDispatch({ type: "status", payload: item.type })
                    }
                  >
                    <Image
                      priority
                      layout={"fixed"}
                      src={
                        selected
                          ? "/checked-square-green.svg"
                          : "/check-square-green.svg"
                      }
                      width={20}
                      height={19}
                      alt={"check icon"}
                    />
                    <Spacer width={8} />
                    <div>{item.text}</div>
                  </div>
                  {index !== statusFilters.length - 1 && <Spacer width={8} />}
                </React.Fragment>
              );
            })}
          </div>
        </FilterSection>

        <Spacer height={39} />

        <FilterSection name={t("galleryAll.mediaType")}>
          <div
            className={clsx("row ai-center flex-wrap", styles.filterButtons)}
          >
            {mediaTypeFilters.map((item, index) => {
              const selected = filters.mediaTypeFilters.includes(item.type);

              return (
                <React.Fragment key={`media_type_filter_${item.type}`}>
                  <div
                    className={clsx(
                      "outlineButton row ai-center",
                      styles.filterItemButton
                    )}
                    onClick={() =>
                      filtersDispatch({
                        type: "media-type",
                        payload: item.type,
                      })
                    }
                  >
                    <Image
                      priority
                      layout={"fixed"}
                      src={
                        selected
                          ? "/checked-square-green.svg"
                          : "/check-square-green.svg"
                      }
                      width={20}
                      height={19}
                      alt={"check icon"}
                    />
                    <Spacer width={8} />
                    <div>{item.text}</div>
                  </div>
                  {index !== mediaTypeFilters.length - 1 && (
                    <Spacer width={8} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </FilterSection>

        <Spacer height={39} />

        <Collections
          filtersDispatch={filtersDispatch}
          selectedCollections={filters.selectedCollections}
        />

        <Spacer height={16} />
        <div className={styles.separator} />
        <Spacer height={8} />
        <div className={"column ai-center"}>
          <div
            className={styles.clearFilter}
            onClick={() => filtersDispatch({ type: "reset" })}
          >
            {t("galleryAll.clearFilter")}
          </div>
        </div>
      </div>
    </a.div>
  );
};

interface CollectionsProps {
  selectedCollections: string[];
  filtersDispatch: Dispatch<FiltersAction>;
}

const Collections: React.FC<CollectionsProps> = ({
  selectedCollections,
  filtersDispatch,
}) => {
  const { t } = useTranslation();
  const [searchCollectionText, setSearchCollectionText] = useState("");
  const [collections, setCollections] = useState<NFTCollection[]>([]);
  const [loadingCollections, setLoadingCollections] = useState(true);
  useEffect(() => {
    getDocs(query(collection(db, "nft-collections")))
      .then((snapshot) => {
        const data = snapshot.docs.map((doc) => doc.data() as NFTCollection);

        setCollections(data);
      })
      .finally(() => {
        setLoadingCollections(false);
      });
  }, []);

  const filteredCollections = useMemo(() => {
    const trimmed = searchCollectionText.toLowerCase().trim();
    if (trimmed.length === 0) return collections;

    return collections.filter((item) =>
      (item.symbol || "OTHERS").toLowerCase().includes(trimmed)
    );
  }, [searchCollectionText, collections]);

  const numberOfItems = useMemo(
    () => collections.reduce((acc, item) => acc + item.count, 0),
    [collections]
  );

  return (
    <FilterSection
      name={`${t("galleryAll.collection.text")} (${formatItems(
        numberOfItems
      )})`}
    >
      <div className={styles.inputContainer}>
        <Image
          priority
          src={"/search.svg"}
          width={16}
          height={16}
          alt={"search collections icon"}
          layout={"fixed"}
        />
        <input
          placeholder={t("galleryAll.collection.search")}
          onChange={(e) => setSearchCollectionText(e.target.value)}
        />
      </div>
      <div className={styles.collections}>
        {loadingCollections && (
          <div className={styles.loadingCollections}>
            {t("galleryAll.loadingCollections")}
          </div>
        )}
        {filteredCollections.map((item, index) => {
          const displayText = item.symbol || "OTHERS";
          const selected = selectedCollections.includes(displayText);

          return (
            <React.Fragment key={item.symbol}>
              <Spacer height={24} />
              <div
                className={"row ai-center cursor-pointer user-select-none"}
                onClick={() => {
                  filtersDispatch({
                    type: "collection",
                    payload: item.symbol,
                  });
                }}
              >
                <Image
                  priority
                  layout={"fixed"}
                  src={
                    selected
                      ? "/checked-square-green.svg"
                      : "/check-square-green.svg"
                  }
                  width={20}
                  height={19}
                  alt={"check icon"}
                />
                <Spacer width={16} />
                <div className={"column"}>
                  <div className={styles.collectionName}>{displayText}</div>
                  <Spacer height={4} />
                  <div className={styles.collectionItems}>
                    {formatItems(item.count)}{" "}
                    {t("galleryAll.item", { count: item.count })}
                  </div>
                </div>
              </div>
              {index === filteredCollections.length - 1 && (
                <Spacer height={24} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </FilterSection>
  );
};

const NFTCounter: React.FC = () => {
  const { t } = useTranslation();
  const [nftsCounter, setNFTCounters] = useState<number | null>(null);
  useEffect(() => {
    getDoc(doc(db, "counters/nft")).then((snapshot) => {
      setNFTCounters(snapshot.data()?.counter ?? 0);
    });
  }, []);

  if (nftsCounter == null) return null;

  return (
    <div className={styles.numberOfItems}>
      {formatItems(nftsCounter)} {t("galleryAll.item", { count: nftsCounter })}
    </div>
  );
};
